import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { Notice } from '../../components/ui/Notice';
import { getHotels } from '../../api/hotels';
import { getRooms } from '../../api/hotels';
import { searchPrice } from '../../api/pricing';
import { createBooking } from '../../api/bookings';
import { Hotel, Room, SearchResult } from '../../types';
import { formatCurrency } from '../../utils/format';
import { useAuth } from '../../hooks/useAuth';

export const SearchBook: React.FC = () => {
  const { user } = useAuth();
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [checkin, setCheckin] = useState('');
  const [checkout, setCheckout] = useState('');
  const [priceResult, setPriceResult] = useState<SearchResult | null>(null);
  const [priceError, setPriceError] = useState('');
  const [step, setStep] = useState<'search' | 'guest' | 'confirm'>('search');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [bookingResult, setBookingResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { getHotels().then(r => setHotels(r.data.results || r.data)); }, []);
  useEffect(() => {
    if (selectedHotel) getRooms(selectedHotel).then(r => setRooms(r.data.results || r.data));
  }, [selectedHotel]);

  const handleSearch = async () => {
    if (!selectedHotel || !selectedRoom || !checkin || !checkout) return;
    setPriceError('');
    setPriceResult(null);
    setLoading(true);
    try {
      const r = await searchPrice({ hotel_id: selectedHotel, room_id: selectedRoom, checkin, checkout });
      setPriceResult(r.data);
      setStep('guest');
    } catch (e: any) {
      setPriceError(e.response?.data?.message || 'Tidak dapat menghitung harga');
    } finally { setLoading(false); }
  };

  const handleBook = async () => {
    setLoading(true);
    try {
      const r = await createBooking({
        hotel_id: selectedHotel, room_id: selectedRoom,
        guest_name: guestName, guest_email: guestEmail, guest_phone: guestPhone,
        checkin_date: checkin, checkout_date: checkout,
      });
      setBookingResult(r.data);
      setStep('confirm');
    } catch (e: any) {
      setPriceError(e.response?.data?.message || 'Gagal membuat booking');
    } finally { setLoading(false); }
  };

  if (step === 'confirm' && bookingResult) {
    return (
      <div>
        <h1 style={{ fontSize: 20, fontWeight: 500, marginBottom: 20 }}>Booking Berhasil</h1>
        <Card>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 4 }}>Booking Dibuat</div>
            <div style={{ fontSize: 13, color: '#888780', marginBottom: 20, fontFamily: 'monospace' }}>{bookingResult.booking_code}</div>
          </div>
          <div style={{ background: '#f8f8f7', borderRadius: 8, padding: 16, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}><span style={{ color: '#888780' }}>Tamu</span><span style={{ fontWeight: 500 }}>{bookingResult.guest_name}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}><span style={{ color: '#888780' }}>Hotel</span><span>{bookingResult.hotel_name}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}><span style={{ color: '#888780' }}>Kamar</span><span>{bookingResult.room_type} · {bookingResult.nights} malam</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', marginTop: 4, borderTop: '0.5px solid rgba(0,0,0,0.1)', fontWeight: 500, fontSize: 15 }}><span>Total</span><span>{formatCurrency(bookingResult.total_price)}</span></div>
          </div>
          <Notice type="warn" style={{ marginTop: 12 }}>Status: Pending Payment — tunggu konfirmasi dari sistem</Notice>
          <button onClick={() => { setStep('search'); setPriceResult(null); setBookingResult(null); setGuestName(''); setGuestEmail(''); setGuestPhone(''); }}
            style={{ marginTop: 12, padding: '8px 16px', background: '#0F6E56', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
            Cari Lagi
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 500, marginBottom: 20 }}>Cari & Booking Hotel</h1>
      {user?.role === 'sub_agent' && <Notice type="warn">Kamu melihat harga setelah markup admin. Base rate hotel tidak ditampilkan.</Notice>}
      <div style={{ display: 'grid', gridTemplateColumns: priceResult ? '1fr 340px' : '1fr', gap: 16 }}>
        <Card title="Cari ketersediaan">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Hotel</label>
              <select value={selectedHotel} onChange={e => { setSelectedHotel(e.target.value); setSelectedRoom(''); }}
                style={{ width: '100%', padding: '8px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, fontSize: 13, background: '#fafaf9' }}>
                <option value="">Pilih hotel</option>
                {hotels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Kamar</label>
              <select value={selectedRoom} onChange={e => setSelectedRoom(e.target.value)} disabled={!selectedHotel}
                style={{ width: '100%', padding: '8px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, fontSize: 13, background: '#fafaf9' }}>
                <option value="">Pilih kamar</option>
                {rooms.map(r => <option key={r.id} value={r.id}>{r.room_type}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Checkin</label>
              <input type="date" value={checkin} onChange={e => setCheckin(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, fontSize: 13, background: '#fafaf9', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Checkout</label>
              <input type="date" value={checkout} onChange={e => setCheckout(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, fontSize: 13, background: '#fafaf9', boxSizing: 'border-box' }} />
            </div>
          </div>
          {priceError && <Notice type="danger">{priceError}</Notice>}
          <button onClick={handleSearch} disabled={loading || !selectedHotel || !selectedRoom || !checkin || !checkout}
            style={{ padding: '9px 20px', background: '#0F6E56', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Mencari...' : 'Cek Harga'}
          </button>

          {step === 'guest' && priceResult && (
            <div style={{ marginTop: 20, paddingTop: 20, borderTop: '0.5px solid rgba(0,0,0,0.08)' }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Data Tamu</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Nama lengkap</label>
                  <input value={guestName} onChange={e => setGuestName(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, fontSize: 13, background: '#fafaf9', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Email</label>
                  <input type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, fontSize: 13, background: '#fafaf9', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Telepon</label>
                  <input value={guestPhone} onChange={e => setGuestPhone(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, fontSize: 13, background: '#fafaf9', boxSizing: 'border-box' }} />
                </div>
              </div>
              <button onClick={handleBook} disabled={loading || !guestName || !guestEmail}
                style={{ marginTop: 12, padding: '9px 20px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                {loading ? 'Memproses...' : 'Buat Booking'}
              </button>
            </div>
          )}
        </Card>

        {priceResult && (
          <Card title="Rincian harga">
            <Notice type="info">Harga ini yang akan dikenakan ke customer kamu.</Notice>
            <div style={{ background: '#f8f8f7', borderRadius: 8, padding: 12, fontSize: 13 }}>
              {priceResult.admin_markup_pct && parseFloat(priceResult.admin_markup_pct) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#888780' }}>
                  <span>Harga dari admin ({priceResult.admin_markup_pct}%)</span>
                  <span>{formatCurrency(priceResult.admin_markup_amount || '0')}</span>
                </div>
              )}
              {priceResult.sub_markup_pct && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#888780' }}>
                  <span>Markup kamu ({priceResult.sub_markup_pct}%)</span>
                  <span>{formatCurrency(priceResult.sub_markup_amount || '0')}</span>
                </div>
              )}
              {priceResult.markup_pct && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#888780' }}>
                  <span>Markup ({priceResult.markup_pct}%)</span>
                  <span>{formatCurrency(priceResult.markup_amount || '0')}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#888780' }}>
                <span>Jumlah malam</span><span>{priceResult.nights} malam</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', marginTop: 4, borderTop: '0.5px solid rgba(0,0,0,0.1)', fontWeight: 500, fontSize: 15 }}>
                <span>Total harga</span><span>{formatCurrency(priceResult.final_price)}</span>
              </div>
            </div>
            <Notice type="warn" style={{ marginTop: 8 }}>Base rate hotel tidak ditampilkan kepada agen.</Notice>
          </Card>
        )}
      </div>
    </div>
  );
};
